import React, { useEffect, useState } from 'react';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { getEquipamentos, createEquipamento, updateEquipamento } from '../../api/equipamentos';
import usePaginacao from '../../hooks/usePaginacao';
import Paginacao from '../../components/common/Paginacao';
import { TIPOS_PROTETOR_AURICULAR } from '../../utils/fitTestOpcoes';

const TIPOS_TESTE_EQ = [
  { value: 'respiratorio', label: 'Respiratório', icon: 'bi-lungs', color: '#198754' },
  { value: 'auditivo',     label: 'Auditivo',     icon: 'bi-ear',   color: '#4361d8' },
];

const EMPTY = { ca: '', tipo_mascara: '', tipo_teste: '', tipo_protetor_auricular: '', descricao: '', fabricante: '' };

function TipoBadge({ tipo }) {
  if (!tipo) return <span className="badge bg-warning text-dark" style={{ fontSize: '.72rem' }}>Não definido</span>;
  const t = TIPOS_TESTE_EQ.find(x => x.value === tipo);
  if (!t) return <span className="text-muted small">{tipo}</span>;
  return (
    <span className="badge rounded-pill" style={{ background: t.color + '18', color: t.color, fontSize: '.75rem', fontWeight: 600 }}>
      <i className={`bi ${t.icon} me-1`}></i>{t.label}
    </span>
  );
}

export default function Equipamentos() {
  const [equipamentos, setEquipamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [error, setError] = useState('');

  const load = () => getEquipamentos().then(setEquipamentos).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.tipo_teste) { setError('Selecione o tipo de teste do equipamento'); return; }
    try {
      await createEquipamento(form);
      setForm(EMPTY);
      setShowForm(false);
      setError('');
      load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao cadastrar equipamento');
    }
  };

  const startEdit = (eq) => {
    setEditId(eq.id_equipamento);
    setEditForm({
      tipo_mascara: eq.tipo_mascara,
      tipo_teste: eq.tipo_teste || '',
      tipo_protetor_auricular: eq.tipo_protetor_auricular || '',
      fabricante: eq.fabricante || '',
      descricao: eq.descricao || '',
    });
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      await updateEquipamento(editId, editForm);
      setEditId(null);
      setError('');
      load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao atualizar equipamento');
    }
  };

  const pag = usePaginacao(equipamentos);

  if (loading) return <LoadingSpinner />;

  return (
    <>
      <PageHeader
        title="Equipamentos"
        subtitle="Cadastro de EPIs utilizados nos testes"
        action={
          <button className="btn-datafit" onClick={() => { setShowForm(!showForm); setEditId(null); }}>
            <i className="bi bi-plus me-2"></i>Novo equipamento
          </button>
        }
      />

      {error && (
        <div className="alert alert-danger py-2 small mb-3">
          <i className="bi bi-exclamation-triangle me-2"></i>{error}
          <button className="btn-close btn-sm float-end" onClick={() => setError('')}></button>
        </div>
      )}

      {showForm && (
        <div className="card-datafit p-4 mb-4">
          <div className="card-header-datafit mb-3" style={{ borderRadius: '0 12px 12px 0', display: 'inline-block' }}>
            Novo equipamento
          </div>
          <form onSubmit={handleCreate}>
            {/* Tipo de teste — primeiro campo, obrigatório */}
            <div className="mb-3">
              <label className="form-label small fw-500">Tipo de teste *</label>
              <div className="d-flex gap-4 mt-1">
                {TIPOS_TESTE_EQ.map(({ value, label, icon, color }) => (
                  <div key={value} className="form-check">
                    <input
                      className="form-check-input" type="radio" id={`new_${value}`}
                      name="new_tipo_teste" value={value}
                      checked={form.tipo_teste === value}
                      onChange={() => setForm(f => ({ ...f, tipo_teste: value }))}
                    />
                    <label className="form-check-label fw-500" htmlFor={`new_${value}`} style={{ color }}>
                      <i className={`bi ${icon} me-1`}></i>{label}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="row g-3">
              <div className="col-md-4">
                <div className="form-floating">
                  <input className="form-control" id="ca" placeholder="CA"
                    value={form.ca} onChange={e => setForm({ ...form, ca: e.target.value })} required />
                  <label htmlFor="ca">CA *</label>
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-floating">
                  <input className="form-control" id="tipo_mascara" placeholder="Tipo de máscara"
                    value={form.tipo_mascara} onChange={e => setForm({ ...form, tipo_mascara: e.target.value })} required />
                  <label htmlFor="tipo_mascara">Tipo de máscara *</label>
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-floating">
                  <input className="form-control" id="fabricante" placeholder="Fabricante"
                    value={form.fabricante} onChange={e => setForm({ ...form, fabricante: e.target.value })} />
                  <label htmlFor="fabricante">Fabricante</label>
                </div>
              </div>
              {form.tipo_teste === 'auditivo' && (
                <div className="col-md-6">
                  <label className="form-label small fw-500">Tipo de protetor auricular</label>
                  <select className="form-select" value={form.tipo_protetor_auricular}
                    onChange={e => setForm({ ...form, tipo_protetor_auricular: e.target.value })}>
                    <option value="">Selecione...</option>
                    {TIPOS_PROTETOR_AURICULAR.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              )}
              <div className="col-12">
                <div className="form-floating">
                  <input className="form-control" id="descricao" placeholder="Descrição"
                    value={form.descricao} onChange={e => setForm({ ...form, descricao: e.target.value })} />
                  <label htmlFor="descricao">Descrição</label>
                </div>
              </div>
            </div>

            <div className="d-flex gap-2 mt-3">
              <button type="submit" className="btn-datafit" style={{ height: 40, fontSize: '.85rem' }}>Salvar</button>
              <button type="button" className="btn btn-outline-secondary btn-sm"
                onClick={() => { setShowForm(false); setError(''); }}>Cancelar</button>
            </div>
          </form>
        </div>
      )}

      <div className="card-datafit">
        <div className="table-responsive">
          <table className="table table-datafit mb-0">
            <thead>
              <tr>
                <th>CA</th>
                <th>Tipo de teste</th>
                <th>Tipo de máscara</th>
                <th>Fabricante</th>
                <th>Descrição</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {equipamentos.length === 0 ? (
                <tr><td colSpan={6} className="text-center text-muted py-4">Nenhum equipamento cadastrado</td></tr>
              ) : pag.paginados.map(eq => (
                <React.Fragment key={eq.id_equipamento}>
                  <tr>
                    <td className="fw-bold">{eq.ca}</td>
                    <td><TipoBadge tipo={eq.tipo_teste} /></td>
                    <td>{eq.tipo_mascara}</td>
                    <td>{eq.fabricante || '—'}</td>
                    <td className="text-muted small">{eq.descricao || '—'}</td>
                    <td>
                      <button className="btn btn-sm btn-outline-primary" title="Editar"
                        onClick={() => editId === eq.id_equipamento ? setEditId(null) : startEdit(eq)}>
                        <i className="bi bi-pencil"></i>
                      </button>
                    </td>
                  </tr>
                  {editId === eq.id_equipamento && (
                    <tr className="table-light">
                      <td colSpan={6} className="p-3">
                        <form onSubmit={handleEdit} className="d-flex gap-2 flex-wrap align-items-end">
                          <div>
                            <label className="form-label small mb-1">Tipo de teste</label>
                            <div className="d-flex gap-3 mt-1">
                              {TIPOS_TESTE_EQ.map(({ value, label, icon, color }) => (
                                <div key={value} className="form-check">
                                  <input className="form-check-input" type="radio"
                                    id={`edit_${value}_${eq.id_equipamento}`}
                                    name={`edit_tipo_${eq.id_equipamento}`} value={value}
                                    checked={editForm.tipo_teste === value}
                                    onChange={() => setEditForm(f => ({ ...f, tipo_teste: value }))} />
                                  <label className="form-check-label small" style={{ color }}
                                    htmlFor={`edit_${value}_${eq.id_equipamento}`}>
                                    <i className={`bi ${icon} me-1`}></i>{label}
                                  </label>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div>
                            <label className="form-label small mb-1">Tipo de máscara</label>
                            <input className="form-control form-control-sm" style={{ width: 180 }}
                              value={editForm.tipo_mascara}
                              onChange={e => setEditForm({ ...editForm, tipo_mascara: e.target.value })} required />
                          </div>
                          {editForm.tipo_teste === 'auditivo' && (
                            <div>
                              <label className="form-label small mb-1">Tipo de protetor</label>
                              <select className="form-select form-select-sm" style={{ width: 200 }}
                                value={editForm.tipo_protetor_auricular}
                                onChange={e => setEditForm({ ...editForm, tipo_protetor_auricular: e.target.value })}>
                                <option value="">—</option>
                                {TIPOS_PROTETOR_AURICULAR.map(t => <option key={t} value={t}>{t}</option>)}
                              </select>
                            </div>
                          )}
                          <div>
                            <label className="form-label small mb-1">Fabricante</label>
                            <input className="form-control form-control-sm" style={{ width: 140 }}
                              value={editForm.fabricante}
                              onChange={e => setEditForm({ ...editForm, fabricante: e.target.value })} />
                          </div>
                          <div>
                            <label className="form-label small mb-1">Descrição</label>
                            <input className="form-control form-control-sm" style={{ width: 220 }}
                              value={editForm.descricao}
                              onChange={e => setEditForm({ ...editForm, descricao: e.target.value })} />
                          </div>
                          <button type="submit" className="btn btn-sm btn-primary">Salvar</button>
                          <button type="button" className="btn btn-sm btn-outline-secondary"
                            onClick={() => setEditId(null)}>Cancelar</button>
                        </form>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <Paginacao {...pag} />
      </div>
    </>
  );
}
